/**
 * API Base URL Configuration
 * 
 * KURALLAR:
 * - Development: REACT_APP_API_URL environment variable (sadece React dev server için)
 * - Production: Electron IPC (window.api.getApiBaseUrl) - ZORUNLU
 * - localhost fallback, defaultDevUrl, otomatik port seçimi TAMAMEN KALDIRILDI
 * 
 * Development: REACT_APP_API_URL=https://notesplus-api.bosph.net/api (veya local override)
 * Production: Electron main process'ten alınır (https://notesplus-api.bosph.net/api)
 */

// Singleton cache
let apiBaseUrl = null;
let apiBaseUrlPromise = null;
let isInitialized = false;

// Internal function to read from Electron IPC (async) - PRODUCTION MODE
const readApiBaseUrlFromElectron = async () => {
  if (typeof window === 'undefined' || !window.api?.getApiBaseUrl) {
    throw new Error('Electron API not available. This should only happen in development.');
  }
  
  try {
    const url = await window.api.getApiBaseUrl();
    if (url && typeof url === 'string' && url.trim() !== '') {
      return url.trim().replace(/\/+$/, '');
    }
    throw new Error('API URL is empty or invalid');
  } catch (error) {
    console.error('❌ [apiConfig] Failed to get API URL from Electron:', error);
    throw error;
  }
};

// Internal function to read from ENV - DEVELOPMENT ONLY
const readApiBaseUrlFromEnv = () => {
  // Only use ENV in development (React dev server)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL.trim().replace(/\/+$/, '');
    if (apiUrl && apiUrl.length > 0) {
      return apiUrl;
    }
  }
  return null;
};

// Initialize API URL - MUST be called before any API calls
export const initializeApiUrl = async () => {
  if (isInitialized && apiBaseUrl) {
    return apiBaseUrl;
  }

  if (apiBaseUrlPromise) {
    return apiBaseUrlPromise;
  }

  apiBaseUrlPromise = (async () => {
    try {
      // Electron IPC (production veya Electron app içinde)
      if (typeof window !== 'undefined' && window.api?.getApiBaseUrl) {
        apiBaseUrl = await readApiBaseUrlFromElectron();
        isInitialized = true;
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [apiConfig] API URL initialized from Electron:', apiBaseUrl);
        }
        return apiBaseUrl;
      }

      // Browser (Web App mode): development ENV önce, sonra hardcoded fallback
      if (typeof window !== 'undefined' && !window.api) {
        const envUrl = readApiBaseUrlFromEnv();
        if (envUrl) {
          apiBaseUrl = envUrl;
          isInitialized = true;
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [apiConfig] API URL initialized from ENV:', apiBaseUrl);
          }
          return apiBaseUrl;
        }

        // Prod ortamında veya webapp olarak çalışırken hardcoded fallback kullan
        apiBaseUrl = 'https://notesplus-api.bosph.net/api';
        isInitialized = true;
        if (process.env.NODE_ENV === 'development') {
          console.log('🌐 [apiConfig] API URL initialized from Browser Fallback:', apiBaseUrl);
        }
        return apiBaseUrl;
      }

      // If we reach here, API URL is not available
      throw new Error('API URL yapılandırılmadı. Electron çalışıyor olmalı veya REACT_APP_API_URL set edilmelidir.');
    } catch (error) {
      console.error('❌ [apiConfig] Failed to initialize API URL:', error);
      throw error;
    } finally {
      apiBaseUrlPromise = null;
    }
  })();

  return apiBaseUrlPromise;
};

// Export function - MUST be async in production
// NOTE: This is a synchronous fallback. Use getApiBaseUrlAsync() in production.
export const getApiBaseUrl = () => {
  if (apiBaseUrl) {
    return apiBaseUrl;
  }

  // In production, we MUST wait for Electron (async)
  if (typeof window !== 'undefined' && window.api?.getApiBaseUrl) {
    throw new Error('API URL not initialized. Call initializeApiUrl() first or use getApiBaseUrlAsync().');
  }

  // Try ENV (for React dev server only)
  const envUrl = readApiBaseUrlFromEnv();
  if (envUrl) {
    apiBaseUrl = envUrl;
    return apiBaseUrl;
  }

  throw new Error('API URL not available. Call initializeApiUrl() first or use getApiBaseUrlAsync().');
};

// Async version - RECOMMENDED for production
export const getApiBaseUrlAsync = async () => {
  if (apiBaseUrl && isInitialized) {
    return apiBaseUrl;
  }

  return await initializeApiUrl();
};
